// SubscriptionGuard.js - The Kill Switch Gatekeeper
// Checks subscription status before allowing access to protected resources

import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useShop } from './App';
import { useAuth } from './App';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './App';

/**
 * SubscriptionGuard Component
 * 
 * Props:
 * - children: Components to render if subscription is active
 * - useAuthShop: If true, uses the shop from auth context instead of URL params
 * - fallbackPath: Custom redirect path (default: suspended page)
 * 
 * Usage:
 * <SubscriptionGuard>
 *   <ProtectedComponent />
 * </SubscriptionGuard>
 */
const SubscriptionGuard = ({ children, useAuthShop = false, fallbackPath = null }) => {
  const shopContext = useShop();
  const authContext = useAuth();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('loading'); // loading, active, suspended, error
  const [shopInfo, setShopInfo] = useState(null);

  useEffect(() => {
    let unsubscribe = null;

    const checkSubscription = async () => {
      // IMMEDIATE BYPASS FOR ADMINS
      if (authContext?.userRole === 'admin' || authContext?.isAdmin) {
        setStatus('active');
        return;
      }

      try {
        // Determine which shop to check
        let shopId = null;
        let shopData = null;

        if (useAuthShop) {
          // Use shop from authenticated user's context
          shopId = authContext?.shopId;
          shopData = authContext?.shopData;
        } else {
          // Use shop from URL params (public intake pages)
          shopId = shopContext?.shopId;
          shopData = shopContext?.shop;
        }

        // No shop ID available
        if (!shopId) {
          console.error('SubscriptionGuard: No shopId available');
          setStatus('error');
          return;
        }

        // If we already have shop data from context, use it
        if (shopData) {
          evaluateShopStatus(shopData);
          return;
        }

        // Otherwise, subscribe to shop document directly
        const shopRef = doc(db, 'shops', shopId);
        unsubscribe = onSnapshot(shopRef, (shopDoc) => {
          if (shopDoc.exists()) {
            const data = { id: shopDoc.id, ...shopDoc.data() };
            evaluateShopStatus(data);
          } else {
            console.error('SubscriptionGuard: Shop not found');
            setStatus('error');
          }
        }, (error) => {
          console.error('SubscriptionGuard: Firestore error', error);
          setStatus('error');
        });

      } catch (error) {
        console.error('SubscriptionGuard: Error checking subscription', error);
        setStatus('error');
      }
    };

    const evaluateShopStatus = (shop) => {
      setShopInfo(shop);

      // Check subscription status
      const shopStatus = shop.status?.toLowerCase();

      switch (shopStatus) {
        case 'active':
          // Also check if subscription is still valid (optional grace period logic)
          if (shop.subscriptionExpiresAt) {
            const expiresAt = shop.subscriptionExpiresAt.toDate?.() || new Date(shop.subscriptionExpiresAt);
            const now = new Date();
            const gracePeriodDays = 3; // 3 day grace period
            const graceDate = new Date(expiresAt);
            graceDate.setDate(graceDate.getDate() + gracePeriodDays);

            if (now > graceDate) {
              console.warn('SubscriptionGuard: Subscription expired beyond grace period');
              setStatus('suspended');
              return;
            }
          }
          setStatus('active');
          break;

        case 'suspended':
        case 'cancelled':
        case 'past_due':
        case 'unpaid':
          setStatus('suspended');
          break;

        case 'trial':
        case 'beta_trial':
          // Check if trial has expired
          if (shop.trialEndsAt) {
            const trialEnds = shop.trialEndsAt.toDate?.() || new Date(shop.trialEndsAt);
            if (new Date() > trialEnds) {
              console.warn('SubscriptionGuard: Beta trial expired');
              setStatus('suspended');
              return;
            }
          }
          setStatus('active');
          break;

        case 'pending':
          // Shop is set up but payment not configured
          setStatus('pending');
          break;

        default:
          console.warn('SubscriptionGuard: Unknown status', shopStatus);
          setStatus('suspended');
      }
    };

    checkSubscription();

    return () => {
      if (unsubscribe) unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAuthShop, shopContext?.shopId, shopContext?.shop, authContext?.shopId, authContext?.shopData]);

  // Re-evaluate when shop data changes
  useEffect(() => {
    if (useAuthShop && authContext?.shopData) {
      const shopStatus = authContext.shopData.status?.toLowerCase();
      if (shopStatus === 'active' || shopStatus === 'trial' || shopStatus === 'beta_trial') {
        setStatus('active');
        setShopInfo(authContext.shopData);
      } else if (shopStatus === 'suspended' || shopStatus === 'cancelled') {
        setStatus('suspended');
        setShopInfo(authContext.shopData);
      }
    }
  }, [useAuthShop, authContext?.shopData]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-slate-700 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-amber-400 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <p className="mt-4 text-slate-400 text-sm">Verifying subscription...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Service Unavailable</h2>
          <p className="text-slate-600 mb-6">
            We couldn't verify the shop's subscription status. Please try again or contact support.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Pending state (needs payment setup)
  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Setup Required</h2>
          <p className="text-slate-600 mb-6">
            This shop needs to complete payment setup before accepting customers.
          </p>
          <button
            onClick={() => navigate('/onboarding')}
            className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all"
          >
            Complete Setup
          </button>
        </div>
      </div>
    );
  }

  // Suspended state - THE KILL SWITCH
  if (status === 'suspended') {
    const shopId = useAuthShop ? authContext?.shopId : shopContext?.shopId;
    const redirectPath = fallbackPath || `/s/${shopId}/suspended`;
    
    return <Navigate to={redirectPath} replace state={{ shopInfo }} />;
  }

  // Active - render protected content
  return children;
};

export default SubscriptionGuard;
